import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { ApiError } from "@/api/fetchApi";
import { useSaveQuestionnaire, useUser } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { copyText } from "@/copy/copy";
import { errorText } from "@/components/ErrorPanel";
import {
  type FhsaChoice,
  fhsaChoiceEdits,
  FhsaTransferDialog,
  fhsaTransferErrors,
} from "@/components/FhsaTransferDialog";
import { Spinner } from "@/components/Spinner";
import { ThemeButton } from "@/components/ThemeButton";
import type { FieldEdit } from "@/generated/core/v1/questionnaire";
import { FieldError } from "@/generated/core/v1/value_source";
import { isSet, type PDate, todayLocal } from "@/plan-logic/lib/dates";
import { buildOnboardingQuestionnaire } from "@/plan-logic/questionnaire/build";
import { priorityFieldErrors } from "@/questionnaire/priorityErrors";
import {
  clearDraft,
  emptyDraft,
  loadDraft,
  members,
  type OnboardingDraft,
  saveDraft,
} from "@/plan-logic/questionnaire/draft";
import { draftSchema, STEP_FIELDS, validateDraft } from "./schema";
import { type OnbNav, OnbNavContext } from "./Screen";
import { ServerErrorsContext } from "./useDraft";
import { HouseholdStep, PrioritiesStep, WelcomeStep } from "./steps/AboutYou";
import {
  BankingStep,
  EverydayAccountsStep,
  InvestmentAccountsStep,
  InvestmentsStep,
  ReviewStep,
  SpendingStep,
} from "./steps/Accounts";
import { BuildingStep, ImpactStep } from "./steps/Finish";
import "@/styles/v47-onboarding.css";
import { DebtsStep, IncomeStep, SafetyStep } from "./steps/Gaps";

/**
 * Onboarding (`#onb`): the prototype's live flow in v47's order (Ian, 2026-11-01): the household
 * first, with sign-up in it, then the priorities; manual entry instead of bank connections, and a
 * real save and plan job at the end.
 * v39: no retirement step (its share and age are on "Retiring comfortably"); the safety net builds.
 * Numbered steps are counted from this list, so the "Step n of N" labels can't drift.
 */
type StepDef = { id: string; n?: number; eyebrow?: string };

export const FLOW: StepDef[] = [
  { id: "welcome", eyebrow: "Welcome" },
  { id: "household", n: 1 },
  { id: "priorities", n: 2 },
  { id: "banking", n: 3 },
  { id: "everyday-accounts", n: 3 },
  { id: "investments", n: 4 },
  { id: "investment-accounts", n: 4 },
  { id: "spending", n: 5 },
  { id: "review", n: 6 },
  { id: "income", n: 7 },
  { id: "debts", n: 8 },
  { id: "safety", n: 9 },
  { id: "building" },
  { id: "plan", eyebrow: "Your plan" },
];

export const TOTAL_STEPS = Math.max(...FLOW.map((s) => s.n ?? 0));
/** Sign-up is on the household step (v47's place for the email): every step after it needs an account. */
const ACCOUNT_AT = FLOW.findIndex((s) => s.id === "household");

export function stepLabel(step: StepDef): string {
  if (step.n) return copyText("onboarding.step", { n: step.n, total: TOTAL_STEPS });
  return step.eyebrow ?? "";
}

function skip(id: string, draft: OnboardingDraft): boolean {
  if (id === "investment-accounts") return draft.noInvestments;
  return id === "building" || id === "plan";
}

/** Which step shows the first problem, so "Build my plan" can send the user back to it. */
function firstInvalidStep(draft: OnboardingDraft, today: PDate): string | undefined {
  const issues = validateDraft(draft, today);
  for (const step of FLOW) {
    const fields = STEP_FIELDS[step.id];
    if (!fields) continue;
    if (issues.some((i) => fields.some((f) => i.path === f || i.path.startsWith(`${f}.`)))) {
      if (step.id === "investment-accounts" && draft.noInvestments) continue;
      return step.id;
    }
  }
  return undefined;
}

const JOB_KEY = "abi.onboardingJob";
/** The first-time home buyer boxes on the "What you owe" step (`FirstTimeBuyerChecks`). */
export const ONB_FIRST_TIME_BOXES = "first-time";

/** Navigation state: what to bring into view on arriving at a step (else the top of the page). */
type Arrival = { show?: string } | null;

export function Onboarding() {
  // The draft is read once, for the signed-in user (or none): wait until auth knows who that is.
  const { ready } = useAuth();
  if (!ready) return <Spinner label="Loading" />;
  return <OnboardingFlow />;
}

function OnboardingFlow() {
  const params = useParams();
  const navigate = useNavigate();
  const show = (useLocation().state as Arrival)?.show;
  const { user, signOut } = useAuth();
  const uid = user?.uid;
  const userQuery = useUser();
  const save = useSaveQuestionnaire();
  const currentDate = userQuery.data?.currentDate;
  const today = useMemo(() => (isSet(currentDate) ? currentDate : todayLocal()), [currentDate]);

  // This user's draft (another user's, on the same browser, isn't loaded).
  const [initial] = useState(() => loadDraft(uid) ?? emptyDraft());
  const form = useForm<OnboardingDraft>({
    defaultValues: initial,
    resolver: zodResolver(draftSchema),
    mode: "onSubmit",
  });

  const [jobId, setJobId] = useState<string | undefined>(
    () => sessionStorage.getItem(JOB_KEY) ?? undefined,
  );
  const [saveError, setSaveError] = useState<string | undefined>();
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);
  // The save refused an FHSA whose holder isn't a first-time home buyer: the three-option dialog,
  // with the field edits the household already chose in it (resent with every retry).
  const [fhsaAsk, setFhsaAsk] = useState<{ errors: FieldError[]; edits: FieldEdit[] } | null>(null);
  // Set while the household is being saved, so the "already has a household" redirect doesn't
  // fire between the save and the move to the Building screen.
  const [finishing, setFinishing] = useState(false);
  // Problems found when "Build my plan" sends the user back to a step, shown until they edit.
  const [issues, setIssues] = useState<Record<string, string>>({});

  const stepId = (params["*"] || "").split("/")[0] || initial.step || "welcome";
  const index = Math.max(
    0,
    FLOW.findIndex((s) => s.id === stepId),
  );
  const step = FLOW[index] ?? FLOW[0];

  // Set on the way into the app: the draft is gone for good, so nothing saves it again (a step's
  // effects can send the user on before this component's own effects have run).
  const done = useRef(false);
  // Keep the draft on this device, as this user's, until the household is saved.
  useEffect(() => {
    const sub = form.watch((values, { name }) => {
      if (!done.current) saveDraft(values as OnboardingDraft, uid);
      if (name !== "step") setIssues((x) => (Object.keys(x).length ? {} : x));
    });
    return () => sub.unsubscribe();
  }, [form, uid]);
  useEffect(() => {
    form.setValue("step", step.id);
    if (!done.current) saveDraft({ ...form.getValues(), step: step.id }, uid);
    if (!show) {
      window.scrollTo({ top: 0 });
      return;
    }
    // after the step has rendered and laid out
    const frame = requestAnimationFrame(() =>
      document.querySelector(`[data-testid="${show}"]`)?.scrollIntoView?.({ block: "start" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [step.id, form, uid, show]);

  const go = useCallback(
    (id: string, showOnArrival?: string, replace = false) =>
      navigate(`/start/${id}`, {
        replace,
        state: showOnArrival ? ({ show: showOnArrival } satisfies Arrival) : undefined,
      }),
    [navigate],
  );

  const nav: OnbNav = useMemo(
    () => ({
      stepId: step.id,
      go,
      back: () => {
        const draft = form.getValues();
        for (let i = index - 1; i >= 0; i--) {
          if (!skip(FLOW[i].id, draft)) return go(FLOW[i].id);
        }
        navigate("/");
      },
      next: async () => {
        const fields = STEP_FIELDS[step.id];
        if (fields && !(await form.trigger(fields as never))) return;
        const draft = form.getValues();
        for (let i = index + 1; i < FLOW.length; i++) {
          if (!skip(FLOW[i].id, draft)) return go(FLOW[i].id);
        }
      },
    }),
    [step.id, index, go, form, navigate],
  );

  const build = useCallback(
    async (extra: FieldEdit[] = []) => {
      const draft = form.getValues();
      const bad = firstInvalidStep(draft, today);
      if (bad) {
        setIssues(Object.fromEntries(validateDraft(draft, today).map((i) => [i.path, i.message])));
        go(bad);
        return;
      }
      setSaveError(undefined);
      setServerErrors([]);
      setFinishing(true);
      const questionnaire = buildOnboardingQuestionnaire(draft, today);
      questionnaire.fieldEdits.push(...extra);
      try {
        const res = await save.mutateAsync({ questionnaire, expectedVersion: 0 });
        setFhsaAsk(null);
        const id = res.planJob?.id;
        if (id) sessionStorage.setItem(JOB_KEY, id);
        setJobId(id);
        go("building");
        return;
      } catch (e) {
        setFhsaAsk(null);
        const fhsa = fhsaTransferErrors(e);
        if (fhsa) {
          setFhsaAsk({ errors: fhsa, edits: extra });
          setFinishing(false);
          return;
        }
        if (e instanceof ApiError && e.isValidation) {
          // An error on a priority's field (a home's down payment below the legal minimum, ...) is
          // shown beside it, on the priorities step.
          const { byField, rest } = priorityFieldErrors(e.fieldErrors, draft.priorities);
          if (Object.keys(byField).length) {
            setServerErrors(rest);
            setIssues(byField);
            setFinishing(false);
            go("priorities");
            return;
          }
          setServerErrors(
            e.fieldErrors.length ? e.fieldErrors : [FieldError.create({ message: e.message })],
          );
          return;
        }
        if (e instanceof ApiError && e.isConflict) {
          setSaveError("You already have a saved household. Open your dashboard to see its plan.");
          go("building");
          return;
        }
        setServerErrors([FieldError.create({ message: errorText(e) })]);
      }
      // Only an error gets here (success returned above), so the redirect may apply again.
      setFinishing(false);
    },
    [form, go, save, today],
  );

  const chooseFhsa = (choice: FhsaChoice) => {
    if (!fhsaAsk) return;
    void build([...fhsaAsk.edits, ...fhsaChoiceEdits(fhsaAsk.errors, choice)]);
  };
  const nameOf = (personUuid: string) =>
    members(form.getValues())
      .find((p) => p.uuid === personUuid)
      ?.givenName.trim() || undefined;

  const enterApp = useCallback(() => {
    done.current = true;
    clearDraft();
    sessionStorage.removeItem(JOB_KEY);
    navigate("/app", { replace: true });
  }, [navigate]);

  // The plan job or plan this device remembers isn't this user's (404): the data was wiped since,
  // or the browser kept it from someone else. With a household, the app shows its plan (or its
  // job); without one, the answers stay and the user builds again from the last step.
  const hasFamily = !!userQuery.data?.hasFamily;
  const startOver = useCallback(() => {
    sessionStorage.removeItem(JOB_KEY);
    setJobId(undefined);
    if (hasFamily) enterApp();
    else go("safety", undefined, true);
  }, [hasFamily, enterApp, go]);
  // Signed-in users with a saved household belong in the app (unless they're finishing here).
  if (
    user &&
    userQuery.data?.hasFamily &&
    !finishing &&
    step.id !== "building" &&
    step.id !== "plan"
  ) {
    return <Navigate to="/app" replace />;
  }
  // Everything after the household step (where sign-up is) needs an account.
  if (!user && index > ACCOUNT_AT) return <Navigate to="/start/household" replace />;

  const progress = Math.round((index / (FLOW.length - 1)) * 100);

  let screen;
  const eyebrow = stepLabel(step);
  switch (step.id) {
    case "welcome":
      screen = <WelcomeStep />;
      break;
    case "priorities":
      screen = <PrioritiesStep eyebrow={eyebrow} today={today} />;
      break;
    case "household":
      screen = <HouseholdStep eyebrow={eyebrow} today={today} />;
      break;
    case "banking":
      screen = <BankingStep eyebrow={eyebrow} />;
      break;
    case "everyday-accounts":
      screen = <EverydayAccountsStep eyebrow={eyebrow} />;
      break;
    case "investments":
      screen = <InvestmentsStep eyebrow={eyebrow} />;
      break;
    case "investment-accounts":
      screen = <InvestmentAccountsStep eyebrow={eyebrow} />;
      break;
    case "spending":
      screen = <SpendingStep eyebrow={eyebrow} />;
      break;
    case "review":
      screen = <ReviewStep eyebrow={eyebrow} />;
      break;
    case "income":
      screen = <IncomeStep eyebrow={eyebrow} />;
      break;
    case "debts":
      screen = <DebtsStep eyebrow={eyebrow} />;
      break;
    case "safety":
      screen = (
        <SafetyStep
          eyebrow={eyebrow}
          onBuild={() => void build()}
          building={save.isPending}
          serverErrors={serverErrors}
        />
      );
      break;
    case "building":
      screen = (
        <BuildingStep
          jobId={jobId}
          saveError={saveError}
          onRetry={() => (saveError ? go("safety") : void build())}
          onStale={startOver}
        />
      );
      break;
    case "plan":
      screen = <ImpactStep onEnter={enterApp} onStale={startOver} />;
      break;
    default:
      screen = <WelcomeStep />;
  }

  return (
    <div id="onb">
      <div className="top">
        <div className="top-in">
          <a className="logo" href="/" aria-label="WealthyPlanet home">
            <span className="wplock" aria-hidden="true" />
          </a>
          <div className="stepno" id="stepno" data-testid="onb-stepno">
            {step.id === "building" ? "" : eyebrow}
          </div>
          <ThemeButton id="thm-onb" />
          {user && (
            // Without a household there is no app shell (and no profile menu) to sign out from.
            <button
              type="button"
              className="onb-signout"
              data-testid="onb-sign-out"
              onClick={async () => {
                await signOut();
                navigate("/", { replace: true });
              }}
            >
              {copyText("nav.signout")}
            </button>
          )}
        </div>
        <div className="bar">
          <i id="prog" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <main>
        <FormProvider {...form}>
          <ServerErrorsContext.Provider value={issues}>
            <OnbNavContext.Provider value={nav}>{screen}</OnbNavContext.Provider>
          </ServerErrorsContext.Provider>
        </FormProvider>
        {fhsaAsk && (
          <FhsaTransferDialog
            errors={fhsaAsk.errors}
            nameOf={nameOf}
            pending={save.isPending}
            onChoose={chooseFhsa}
            onFix={() => {
              setFhsaAsk(null);
              go("debts", ONB_FIRST_TIME_BOXES);
            }}
            onClose={() => setFhsaAsk(null)}
          />
        )}
      </main>
    </div>
  );
}
